/**
 * Concordium lookups a phone can make straight from the browser (both send
 * `Access-Control-Allow-Origin: *`; checked 2026-10-23):
 *
 *  - CCDScan's public GraphQL API (`/api/graphql`): sender, block, finality,
 *    the DataRegistered event's bytes;
 *  - Concordium's wallet-proxy (`/v0/submissionStatus/<hash>`): sender,
 *    status, registered data — an independent second source.
 *
 * Neither is our server. `fetch` is injected so Node tests can use the same code.
 */
export type Network = 'mainnet' | 'testnet';

export const ENDPOINTS: Record<Network, { ccdscan: string; walletProxy: string; explorer: string }> = {
  mainnet: {
    ccdscan: 'https://api-ccdscan.mainnet.concordium.software/api/graphql',
    walletProxy: 'https://wallet-proxy.mainnet.concordium.software',
    explorer: 'https://ccdscan.io',
  },
  testnet: {
    ccdscan: 'https://api-ccdscan.testnet.concordium.com/api/graphql',
    walletProxy: 'https://wallet-proxy.testnet.concordium.com',
    explorer: 'https://testnet.ccdscan.io',
  },
};

export type ChainTx = {
  source: string;
  txHash: string;
  sender: string;
  finalized: boolean;
  success: boolean;
  dataHex: string | null;
  blockHash: string | null;
  blockHeight: number | null;
  blockTime: string | null;
};

export type Fetch = (url: string, init?: { method?: string; headers?: Record<string, string>; body?: string; signal?: AbortSignal }) => Promise<{ ok: boolean; status: number; json(): Promise<unknown> }>;

const QUERY = `query($h:String!){transactionByTransactionHash(transactionHash:$h){transactionHash senderAccountAddress{asString} block{blockHash blockHeight blockSlotTime finalized} result{__typename ... on Success{events{nodes{__typename ... on DataRegistered{dataAsHex}}}}}}}`;

export function explorerUrl(network: Network, txHash: string): string {
  return `${ENDPOINTS[network].explorer}/?dcount=1&dentity=transaction&dhash=${txHash}`;
}

/**
 * The lookup does not know the transaction (yet): right after a wallet sends it, CCDScan and the wallet-proxy
 * need a few seconds to index and finalize it. Callers treat this as "pending", never as an error to show.
 */
export class TxNotFound extends Error {
  readonly notFound = true;
  constructor(source: string) {
    super(`${source} does not know this transaction yet`);
  }
}
export const isTxNotFound = (e: unknown): e is TxNotFound => !!e && typeof e === 'object' && (e as { notFound?: unknown }).notFound === true;

function timeout(ms: number): AbortSignal | undefined {
  return typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal ? AbortSignal.timeout(ms) : undefined;
}

export async function lookupCcdScan(network: Network, txHash: string, fetchFn: Fetch, ms = 15000): Promise<ChainTx> {
  const res = await fetchFn(ENDPOINTS[network].ccdscan, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ query: QUERY, variables: { h: txHash } }),
    signal: timeout(ms),
  });
  if (res.status === 404) throw new TxNotFound('CCDScan');
  if (!res.ok) throw new Error(`CCDScan answered HTTP ${res.status}`);
  type R = {
    data?: {
      transactionByTransactionHash?: {
        transactionHash: string;
        senderAccountAddress?: { asString: string } | null;
        block?: { blockHash: string; blockHeight: number; blockSlotTime: string; finalized: boolean };
        result?: { __typename: string; events?: { nodes: Array<{ __typename: string; dataAsHex?: string }> } };
      } | null;
    };
    errors?: Array<{ message: string }>;
  };
  const body = (await res.json()) as R;
  const tx = body.data?.transactionByTransactionHash;
  // CCDScan answers an unknown (not yet indexed) hash with null or "Could not find resource": not found, not an error.
  if (!tx) {
    const msg = body.errors?.[0]?.message ?? '';
    if (!msg || /not find|not found|no such|unknown/i.test(msg)) throw new TxNotFound('CCDScan');
    throw new Error(`CCDScan: ${msg}`);
  }
  const event = tx.result?.events?.nodes.find((n) => n.__typename === 'DataRegistered');
  return {
    source: 'CCDScan',
    txHash: tx.transactionHash,
    sender: tx.senderAccountAddress?.asString ?? '',
    finalized: !!tx.block?.finalized,
    success: tx.result?.__typename === 'Success',
    dataHex: event?.dataAsHex ?? null,
    blockHash: tx.block?.blockHash ?? null,
    blockHeight: tx.block?.blockHeight ?? null,
    blockTime: tx.block?.blockSlotTime ?? null,
  };
}

export async function lookupWalletProxy(network: Network, txHash: string, fetchFn: Fetch, ms = 15000): Promise<ChainTx> {
  const res = await fetchFn(`${ENDPOINTS[network].walletProxy}/v0/submissionStatus/${txHash}`, { signal: timeout(ms) });
  if (res.status === 404) throw new TxNotFound('The Concordium wallet-proxy');
  if (!res.ok) throw new Error(`wallet-proxy answered HTTP ${res.status}`);
  const b = (await res.json()) as {
    status?: string; outcome?: string; sender?: string; registeredData?: string; blockHashes?: string[]; transactionHash?: string;
  };
  if (!b.status || b.status === 'absent') throw new TxNotFound('The Concordium wallet-proxy');
  return {
    source: 'Concordium wallet-proxy',
    txHash: b.transactionHash ?? txHash,
    sender: b.sender ?? '',
    finalized: b.status === 'finalized',
    success: b.outcome === 'success',
    dataHex: b.registeredData ?? null,
    blockHash: b.blockHashes?.[0] ?? null,
    blockHeight: null,
    blockTime: null,
  };
}

/**
 * CCDScan first, the wallet-proxy if CCDScan fails or does not know it yet. Throws `TxNotFound` only when
 * neither knows the transaction (it was just sent: pending), and a plain error when both lookups broke.
 */
export async function lookupTx(network: Network, txHash: string, fetchFn: Fetch, ms = 15000): Promise<ChainTx> {
  let first: unknown;
  try {
    return await lookupCcdScan(network, txHash, fetchFn, ms);
  } catch (e) {
    first = e;
  }
  try {
    return await lookupWalletProxy(network, txHash, fetchFn, ms);
  } catch (e) {
    if (isTxNotFound(e) || isTxNotFound(first)) throw isTxNotFound(e) ? e : first;
    throw e;
  }
}

export type AccountRecord = { txHash: string; dataHex: string; finalized: boolean; blockTime: string | null };

const ACCOUNT_QUERY = `query($a:String!,$after:String){accountByAddress(accountAddress:$a){transactions(first:50,after:$after){pageInfo{hasNextPage endCursor} nodes{transaction{transactionHash block{blockSlotTime finalized} result{__typename ... on Success{events{nodes{__typename ... on DataRegistered{dataAsHex}}}}}}}}}}`;

/**
 * The account's RegisterData records, newest first, from CCDScan. Stops after
 * the transaction `stopAt` (inclusive) or `maxPages` pages of 50: records
 * older than a binding cannot revoke or supersede it.
 */
export async function listAccountRecords(
  network: Network,
  account: string,
  fetchFn: Fetch,
  opts: { stopAt?: string; maxPages?: number; ms?: number } = {},
): Promise<{ records: AccountRecord[]; reachedStop: boolean; complete: boolean }> {
  const records: AccountRecord[] = [];
  let after: string | null = null;
  for (let page = 0; page < (opts.maxPages ?? 10); page++) {
    const res = await fetchFn(ENDPOINTS[network].ccdscan, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ query: ACCOUNT_QUERY, variables: { a: account, after } }),
      signal: timeout(opts.ms ?? 15000),
    });
    if (!res.ok) throw new Error(`CCDScan answered HTTP ${res.status}`);
    type R = {
      data?: {
        accountByAddress?: {
          transactions: {
            pageInfo: { hasNextPage: boolean; endCursor: string | null };
            nodes: Array<{ transaction: { transactionHash: string; block?: { blockSlotTime: string; finalized: boolean }; result?: { __typename: string; events?: { nodes: Array<{ __typename: string; dataAsHex?: string }> } } } }>;
          };
        } | null;
      };
      errors?: Array<{ message: string }>;
    };
    const body = (await res.json()) as R;
    const txs = body.data?.accountByAddress?.transactions;
    if (!txs) throw new Error(body.errors?.[0]?.message ?? 'CCDScan does not know this account');
    for (const n of txs.nodes) {
      const t = n.transaction;
      const ev = t.result?.__typename === 'Success' ? t.result.events?.nodes.find((e) => e.__typename === 'DataRegistered') : undefined;
      if (ev?.dataAsHex) records.push({ txHash: t.transactionHash, dataHex: ev.dataAsHex, finalized: !!t.block?.finalized, blockTime: t.block?.blockSlotTime ?? null });
      if (opts.stopAt && t.transactionHash === opts.stopAt) return { records, reachedStop: true, complete: true };
    }
    if (!txs.pageInfo.hasNextPage) return { records, reachedStop: false, complete: true };
    after = txs.pageInfo.endCursor;
  }
  return { records, reachedStop: false, complete: false };
}
